import { MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material'
import Downshift, {
  ControllerStateAndHelpers,
  GetItemPropsOptions,
  GetMenuPropsOptions,
  GetPropsCommonOptions,
} from 'downshift'
import * as _ from 'lodash'
import { MuiChipsInput } from 'mui-chips-input'
import * as React from 'react'

type GetMenuPropsFn = (
  options?: GetMenuPropsOptions,
  otherOptions?: GetPropsCommonOptions
) => any

type GetItemPropsFn = (options: GetItemPropsOptions<any>) => any

const getItemsDebounceTimeout = 500

const downshiftResetState = {
  isOpen: false,
}

interface ITypeAheadFieldFilterProps<T> {
  fullWidth?: boolean
  getName: (item: T) => string
  getValue: (item: T) => string
  getItems: (filter: string) => void
  items: T[]
  label: string
  onChipChange: (items: T[]) => void
  required?: boolean
  selectedItems: T[]
}

interface ITypeAheadFieldFilterState {
  downshiftInputValue: string
}

class TypeAheadFieldFilter<T> extends React.Component<
  ITypeAheadFieldFilterProps<T>,
  ITypeAheadFieldFilterState
> {
  private inputRef: HTMLDivElement | null = null

  constructor(props: ITypeAheadFieldFilterProps<T>) {
    super(props)
    this.state = {
      downshiftInputValue: '',
    }
  }

  private get currentFormValues() {
    return this.props.selectedItems
  }

  private setInputRef = (element: HTMLDivElement | null): void => {
    this.inputRef = element
  }

  public render() {
    const { fullWidth, label, required } = this.props

    return (
      <Downshift
        inputValue={this.state.downshiftInputValue}
        onChange={this.onExistingSelected}
        itemToString={this.itemToString}
        onInputValueChange={this.onInputChanged}
        onOuterClick={this.onOuterClick}
      >
        {({ getInputProps, getMenuProps, getItemProps, highlightedIndex, isOpen }) => {
          const inputProps = getInputProps()
          const onKeyDown = inputProps.onKeyDown as
            | React.KeyboardEventHandler<HTMLDivElement>
            | undefined
          const { ref, onKeyDown: _, ...filteredInputProps } = inputProps as any
          return (
            <div>
              <MuiChipsInput
                {...filteredInputProps}
                InputLabelProps={{ required }}
                label={label}
                fullWidth={fullWidth}
                value={this.currentFormValues.map(this.props.getName)}
                onChange={(newChips) => {
                  // Handle chip changes from MuiChipsInput
                  const { getName } = this.props
                  const newValues = this.currentFormValues.filter((item) =>
                    newChips.includes(getName(item))
                  )
                  this.props.onChipChange(newValues)
                }}
                onInputChange={(value) => {
                  if (typeof value === 'string') {
                    this.setState({ downshiftInputValue: value })
                    this.onInputChanged(value)
                  }
                }}
                onKeyDown={onKeyDown}
                inputRef={this.setInputRef}
                variant="outlined"
              />
              {this.renderSuggestions(
                getMenuProps,
                getItemProps,
                isOpen,
                highlightedIndex
              )}
            </div>
          )
        }}
      </Downshift>
    )
  }

  public itemToString = (item: T) => {
    return this.props.getName(item)
  }

  private onOuterClick = (helpers: ControllerStateAndHelpers<T>) => {
    helpers.setState(downshiftResetState)
    this.setState({ downshiftInputValue: '' })
  }

  private onExistingSelected = (
    selectedItem: T,
    helpers: ControllerStateAndHelpers<T>
  ) => {
    this.addChip(selectedItem)
    helpers.setState(downshiftResetState)
    this.setState({ downshiftInputValue: '' })
  }

  private addChip = (itemToAdd: T) => {
    if (itemToAdd !== null) {
      const newValues = this.currentFormValues.concat([itemToAdd])
      this.props.onChipChange(newValues)
    }
  }

  private onInputChanged = (inputValue: string) => {
    if (typeof inputValue === 'string') {
      _.debounce(() => {
        if (this.props.getItems && typeof inputValue === 'string') {
          this.props.getItems(inputValue)
        }
      }, getItemsDebounceTimeout)()
    }
  }

  private filterSelectedFromOptions = () => {
    const { getValue } = this.props
    const chipValues = this.currentFormValues.map(getValue)
    return (item: T) => {
      return !chipValues.some((cv) => getValue(item) === cv)
    }
  }

  private renderSuggestions(
    getMenuProps: GetMenuPropsFn,
    getItemProps: GetItemPropsFn,
    isOpen: boolean,
    highlightedIndex: number | null
  ) {
    const { items = [] } = this.props
    const menuProps = getMenuProps({}, { suppressRefError: true })
    const menuItems = items
      .filter(this.filterSelectedFromOptions())
      .map(this.renderMenuItem(getItemProps, highlightedIndex))

    const noSuggestionsMessage = (
      <Typography sx={{ fontStyle: 'italic', padding: '11px 16px' }} variant="body2">
        No suggestions found. Press Enter to create a new entry
      </Typography>
    )

    const suggestions = <MenuList {...menuProps}>{menuItems}</MenuList>
    return (
      <div>
        <Popper
          sx={{ width: 'auto !important', zIndex: 9999 }}
          open={isOpen}
          keepMounted={false}
          anchorEl={this.inputRef}
          placement="bottom"
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
              },
            },
          ]}
          style={{
            width: this.inputRef ? this.inputRef.clientWidth : undefined,
          }}
        >
          <Paper>{menuItems.length > 0 ? suggestions : noSuggestionsMessage}</Paper>
        </Popper>
      </div>
    )
  }

  private renderMenuItem =
    (getItemProps: GetItemPropsFn, highlightedIndex: number | null) =>
    (item: T, index: number) => {
      const { getName, getValue } = this.props
      const itemProps = getItemProps({ key: getValue(item), item })
      const isSelected = highlightedIndex === index
      return (
        <MenuItem {...itemProps} selected={isSelected}>
          {getName(item)}
        </MenuItem>
      )
    }
}

export default TypeAheadFieldFilter
